import { Box, Button, Grid, Group, Modal, NumberInput, Text, Textarea, TextInput } from '@mantine/core';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';

const CIDR_SHAPE = /^[0-9a-fA-F:.]+\/\d{1,3}$/;

const FormRow = ({ label, children }) => (
  <Grid>
    <Grid.Col span={{ base: 12, sm: 4 }}>
      <Text size="sm" fw={500}>
        {label}
      </Text>
    </Grid.Col>
    <Grid.Col span={{ base: 12, sm: 8 }}>{children}</Grid.Col>
  </Grid>
);

const BotIpRangeModal = ({ botIpRange: passedBotIpRange, onClose, onSave }) => {
  const fetch = useFetch();
  const [botIpRange, setBotIpRange] = useState(passedBotIpRange);
  const [saveError, setSaveError] = useState();

  useEffect(() => {
    setBotIpRange(passedBotIpRange);
    setSaveError(undefined);
  }, [passedBotIpRange]);

  if (botIpRange === undefined) {
    return '';
  }

  const cidr = botIpRange.cidr || '';
  const label = botIpRange.label || '';
  const cidrLooksValid = CIDR_SHAPE.test(cidr.trim());

  const save = (event) => {
    event.preventDefault();
    setSaveError(undefined);

    const payload = {
      botIpRange: {
        cidr: cidr.trim(),
        label: label.trim(),
        notes: (botIpRange.notes || '').trim() || null,
        domainId: botIpRange.domainId || null,
      },
    };
    const request = botIpRange.id
      ? fetch(`/api/admin/botIpRanges/${botIpRange.id}`, { method: 'PUT', body: JSON.stringify(payload) })
      : fetch('/api/admin/botIpRanges', { method: 'POST', body: JSON.stringify(payload) });

    request
      .then(({ botIpRange: saved }) => {
        onSave(saved);
        onClose();
      })
      .catch(setSaveError);
  };

  return (
    <Modal
      opened={!!passedBotIpRange}
      onClose={onClose}
      size="lg"
      title={`${botIpRange.id ? 'Edit' : 'Add'} bot IP range`}
    >
      <Box component="form" ta="left" onSubmit={save}>
        <FormRow label="CIDR*">
          <TextInput
            name="cidr"
            placeholder="43.172.0.0/15"
            required
            error={cidr !== '' && !cidrLooksValid ? 'Use address/prefix, e.g. 202.46.62.0/24' : undefined}
            value={cidr}
            onChange={(event) => setBotIpRange({ ...botIpRange, cidr: event.target.value })}
          />
        </FormRow>
        <FormRow label="Label*">
          <TextInput
            name="label"
            placeholder="Tencent Cloud fleet"
            required
            value={label}
            onChange={(event) => setBotIpRange({ ...botIpRange, label: event.target.value })}
          />
        </FormRow>
        <FormRow label="Domain ID">
          <NumberInput
            name="domainId"
            placeholder="Empty applies to every domain"
            allowDecimal={false}
            allowNegative={false}
            hideControls
            value={botIpRange.domainId ?? ''}
            onChange={(domainId) => setBotIpRange({ ...botIpRange, domainId: domainId === '' ? null : domainId })}
          />
        </FormRow>
        <FormRow label="Notes">
          <Textarea
            name="notes"
            rows={4}
            placeholder="Where the fleet was seen, and the audit that found it"
            value={botIpRange.notes || ''}
            onChange={(event) => setBotIpRange({ ...botIpRange, notes: event.target.value })}
          />
        </FormRow>
        {saveError && <ErrorMessage message={saveError.message} />}
        <Group justify="flex-end" gap="xs" py="md">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!cidrLooksValid || label.trim() === ''}>
            <IconDeviceFloppy size={14} /> Save
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};

export default BotIpRangeModal;
